import { useCallback, useContext, memo } from 'react';
import styled from '@emotion/styled';
import type { Chat } from '@trinity/chat';
import { ChatCitationType, ChatHintType, ChatResponseType } from '@trinity/chat';
import { CapabilitiesContext } from '@trinity/chat-components/components/BrandInsights/context';
import { ChatResponsePane } from '@trinity/chat-components/components/Chat/ResponsePane';
import { OpenQueryFeedbackModalContext } from '@trinity/chat-components/components/Chat.Feedback/context.js';
import {
  StarRating,
  useUpdateQueryRatingMutation,
} from '@trinity/chat-components/components/Chat.Feedback/index.js';
import { useShareChatModal } from '@trinity/chat-components/components/Chat.History/Modal.ShareChat.js';
import { MessageCitations } from '@trinity/chat-components/components/Chat.Message.Citations/index.js';
import { useChatApiClient, useGroupFeatures } from '@trinity/chat-components/containers/Chat';
import { useCopyToClipboard } from '@trinity/chat-components/hooks/useCopyToClipboard';
import { ActiveChatSessionContext, MessageCitationsToggleContext } from './context';
import { useChatPermissions } from './hooks/useChatPermissions';
import { MessageButtons } from './Message.Buttons';
import { CopyButton } from './Message.CopyToClipboard';
import { DownloadChartsButton } from './Message.Download.Chart';
import { GoToTraceUrl } from './Message.GoToTraceUrl';
import { ReadAloudButton } from './Message.ReadAloud';
import { ShareButton } from './Message.ShareChatThread';
import { RelatedQuestionsPane } from './QueryHints';
import { QueryMetadataPane } from './QueryMetadata';
type Props = {
  item: Chat.Query;
  numCitations: number;
};

function countCitationSnippets(citations: Chat.Query['citations']): number {
  return citations.reduce((total, citation) => {
    if (citation.type === ChatCitationType.DataAgentQueryResults) {
      return total + 1;
    }

    const amt = citation.snippets.length > 0 ? citation.snippets.length : 1;
    return total + amt;
  }, 0);
}

function buildFollowupHints(item: Chat.Query) {
  const hintType =
    item.response.type === ChatResponseType.BIChartQuantAnalyses ? ChatHintType.Chart : ChatHintType.Text;
  return item.suggestedFollowups.map((f) => ({
    displayValue: f,
    type: hintType,
  }));
}

function buildViewFlags(
  item: Chat.Query,
  chatPermissions: { canModify: boolean },
  capabilities: { canViewMetadata: boolean; canViewTraces: boolean }
) {
  return {
    rating: chatPermissions.canModify,
    followUps: chatPermissions.canModify && !!item.suggestedFollowups.length,
    metadata: capabilities.canViewMetadata && !!item.metadata,
    trace: capabilities.canViewTraces && !!item.langfuseTraceUrl,
  };
}

function shouldShowExportChart(exportChatData: boolean | undefined, item: Chat.Query) {
  const chartResponseTypes = new Set<ChatResponseType>([
    ChatResponseType.BIChartQuantAnalyses,
    ChatResponseType.Chart,
  ]);

  return (
    exportChatData &&
    !!item.response &&
    chartResponseTypes.has(item.response.type)
  );
}

export const SystemUserMessageFooter = ({ item, numCitations }: Props) => {
  const chat = useContext(ActiveChatSessionContext);
  const chatPermissions = useChatPermissions();
  const capabilities = useContext(CapabilitiesContext);
  const [citationsOpen, toggleCitationsOpen] = useContext(MessageCitationsToggleContext);

  const { copyChatMessages, shareChats, exportChatData, followUps } = useGroupFeatures();

  const { mutateAsync: updateQueryRating } = useUpdateQueryRatingMutation({
    chatInstanceId: chat.id,
    queryIdentifier: item.identifier,
  });

  const openFeedbackModal = useContext(OpenQueryFeedbackModalContext);

  const copyToClipboard = useCopyToClipboard();

  const [toggleShareModel, ShareModal] = useShareChatModal();

  const handleShareChatThread = useCallback(() => {
    toggleShareModel();
  }, [toggleShareModel]);

  const handleRatingClicked = useCallback(
    (value: Chat.Rating) => {
      if (value <= 3) {
        return openFeedbackModal({
          queryIdentifier: item.identifier,
          chatInstanceId: chat.id,
          rating: value,
        });
      }

      return updateQueryRating(value);
    },
    [item.identifier, updateQueryRating, openFeedbackModal, chat.id]
  );

  const apiClient = useChatApiClient();

  const getResponseText = useCallback(async () => {
    return apiClient.queries.getQueryResponseText({
      chatInstanceId: chat.id,
      queryId: item.id,
      tts: false,
    });
  }, [apiClient, chat.id]);

  const handleCopy = useCallback(() => {
    getResponseText().then((text) => {
      if (text) {
        copyToClipboard(text);
      }
    });
  }, [copyToClipboard, getResponseText]);

  const view = buildViewFlags(item, chatPermissions, capabilities);
  const showExportChart = shouldShowExportChart(exportChatData, item);

  return (
    <>
      <Layer2>
        <ActionsContainer>
          <ActionsLeft>
            {shareChats && <ShareButton onClick={handleShareChatThread} />}
            <ShareModal chatInstance={chat} />

            <MessageButtons item={item} />
            {copyChatMessages && <CopyButton onClick={handleCopy} />}

            {showExportChart && <DownloadChartsButton />}

            <ReadAloudButton queryId={item.id} />
          </ActionsLeft>

          <ActionsRight>
            {view.rating && <StarRating value={item.rating} onChange={handleRatingClicked} />}
            {view.trace && <GoToTraceUrl url={item.langfuseTraceUrl} />}
          </ActionsRight>
        </ActionsContainer>
      </Layer2>
      {!!numCitations && (
        <StyledCitationsPane
          title={`Sources (${countCitationSnippets(item.citations)})`}
          open={citationsOpen}
          toggleOpen={toggleCitationsOpen}
        >
          <StyledMessageCitations items={item.citations} />
        </StyledCitationsPane>
      )}
      {followUps && view.followUps && item.suggestedFollowups.length > 0 && (
        <StyledRelatedQuestions
          profileKey="general-query"
          queryIdentifier={item.identifier}
          hints={buildFollowupHints(item)}
        />
      )}
      {view.metadata &&
        (item.metadata.rewrittenQuery || item.metadata.evaluation || item.metadata.classifications) && (
          <StyledMetadata metadata={item.metadata} />
        )}
    </>
  );
};

type ContainerProps = {
  children: React.ReactNode;
  className?: string;
};

export const SystemUserMessageFooterContainer = memo(({ className, children }: ContainerProps) => {
  return (
    <Root className={className}>
      <Wrap>{children}</Wrap>
    </Root>
  );
});

const Root = styled.div`
  margin-top: 15px;
`;

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledRelatedQuestions = styled(RelatedQuestionsPane)`
  margin-top: 15px;
`;

const StyledCitationsPane = styled(ChatResponsePane)`
  margin-top: 15px;
`;

const StyledMessageCitations = styled(MessageCitations)`
  margin-top: 10px;
`;

const StyledMetadata = styled(QueryMetadataPane)`
  margin-top: 15px;
`;

const Layer2 = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

const ActionsContainer = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  rowGap: 8,
  width: '100%',
  containerType: 'inline-size',
  containerName: 'message-actions',
});

const ActionsLeft = styled.div({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: 10,
  rowGap: 8,
});

const ActionsRight = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  marginLeft: 'auto',
});
